import { useId } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { storageCopy } from "../utils/storagePresentation";

import { largestFilesUrl } from "../utils/largestFilesUrl";
import { cn } from "@/lib/utils";

function StorageUpgradeAction({ actionId, banner }: { actionId: string; banner: boolean }) {
  const { language } = useLanguage();
  const words = storageCopy[language];
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canViewBilling = permissions.isOrgOwner || permissions.isOrgAdmin;
  const restrictionId = useId();
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={banner ? "destructive" : "ghost"}
        className={banner ? "h-auto px-[14px] py-[7px] disabled:opacity-100" : undefined}
        disabled={!canViewBilling}
        aria-describedby={canViewBilling ? undefined : restrictionId}
        onClick={() => navigate("/billing")}
        data-storage-action={actionId}
      >
        {words.more}
      </Button>
      {!canViewBilling && (
        <p id={restrictionId} className="basis-full text-xs">
          {words.moreRestricted}
        </p>
      )}
    </>
  );
}

export function StorageActions({
  buildingId,
  actionId,
  variant = "bubble",
}: {
  buildingId: string;
  actionId: string;
  variant?: "bubble" | "banner";
}) {
  const { language } = useLanguage();
  const words = storageCopy[language];
  const navigate = useNavigate();
  const location = useLocation();
  const banner = variant === "banner";
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        banner &&
          "ml-auto max-w-full justify-end gap-[8px] @lg/storage-banner:shrink-0 @lg/storage-banner:flex-nowrap"
      )}
    >
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={
          banner
            ? "h-auto border-amber-600/50 bg-transparent px-[14px] py-[7px] text-amber-800 hover:bg-amber-500/10 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
            : undefined
        }
        onClick={() => navigate(largestFilesUrl(buildingId, location.search))}
      >
        {words.largest}
      </Button>
      <StorageUpgradeAction actionId={actionId} banner={banner} />
    </div>
  );
}
